import apiClient from "./apiClient";
import { calculateAge } from "../utils/calculateAge";

const BASE = "/api/Patient";

// The front desk enters age in years, not a birthdate. The save proc derives the stored
// Age from DateOfBirth on every insert/update and ignores any `age` in the payload
// (HANDOVER_FRONTEND 4.md, 2026-08-22 age fix), so saving without a dateOfBirth blanks the
// patient's age everywhere it's read — the patient list, the profile, and the Daily
// Report's age column, which is DATEDIFF'd off DateOfBirth too.
//
// So always send a dateOfBirth: keep the one already on file when it still agrees with the
// age shown in the form (never round a real birthdate away), otherwise derive a nominal one
// from the entered age. The derived date lands on today's calendar day, so the age stays
// right until its anniversary and then ticks up — the correct behaviour for an age captured
// today. DateOfBirth stays the single source of truth server-side, and the age-only form is
// unchanged for the user.
const dobForAge = (age, existingDob) => {
  const years = Number(age);
  if (age === "" || age === null || age === undefined || Number.isNaN(years)) return existingDob || null;
  if (existingDob && calculateAge(existingDob) === years) return existingDob;
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().split("T")[0];
};

// API uses patientID/alternateMobile/dateOfBirth/patientCategory;
// the rest of the app (form, list, redux) uses id/altMobile/dob/category.
const fromApiPatient = (p) => ({
  id: p.patientID,
  opNumber: p.opNumber,
  patientName: p.patientName,
  address: p.address,
  mobileNumber: p.mobileNumber,
  altMobile: p.alternateMobile,
  dob: p.dateOfBirth ? p.dateOfBirth.split("T")[0] : "",
  age: p.age,
  gender: p.gender,
  category: p.patientCategory,
  email: p.email,
  medicalHistory: p.medicalHistory,
  allergies: p.allergies,
  remarks: p.remarks,
  createdDate: p.createdDate,
});

// opNumber is included only when the user supplied one manually (PatientModal's
// "Auto-generate" toggle) — the API accepts an optional opNumber, validates it's unique,
// and auto-generates one (OP-yyyyMM-XXXX) when it's blank/omitted.
// The patient form collects age directly rather than date of birth — see dobForAge above
// for why a dateOfBirth is still derived and sent. `age` is sent alongside it purely for
// forward compatibility; the save proc ignores it today and recomputes Age from
// dateOfBirth, so the two can never disagree.
const toApiPatient = (f) => ({
  ...(f.opNumber ? { opNumber: f.opNumber } : {}),
  patientName: f.patientName,
  address: f.address,
  mobileNumber: f.mobileNumber,
  alternateMobile: f.altMobile,
  dateOfBirth: dobForAge(f.age, f.dob),
  age: f.age ? Number(f.age) : null,
  gender: f.gender,
  patientCategory: f.category,
  email: f.email,
  medicalHistory: f.medicalHistory,
  allergies: f.allergies,
  remarks: f.remarks,
});

export const getPatients = async (params) => {
  const res = await apiClient.get(`${BASE}/all`, { params });
  return { ...res, data: (res.data || []).map(fromApiPatient) };
};

export const getPatient = async (id) => {
  const res = await apiClient.get(`${BASE}/${id}`);
  return { ...res, data: fromApiPatient(res.data) };
};

// create and update both use /save — include patientID in body for update (0/omit for create)
export const createPatient = async (data) => {
  const res = await apiClient.post(`${BASE}/save`, toApiPatient(data));
  return { ...res, data: fromApiPatient(res.data) };
};

export const updatePatient = async (id, data) => {
  const res = await apiClient.post(`${BASE}/save`, { ...toApiPatient(data), patientID: id });
  return { ...res, data: fromApiPatient(res.data) };
};

export const deletePatient = (id) => apiClient.delete(`${BASE}/${id}`);

// search by mobile number (exact match, path param) — returns a single patient, not a list
export const searchPatients = async (query) => {
  const res = await apiClient.get(`${BASE}/search/${encodeURIComponent(query)}`);
  const raw = res.data;
  const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
  return { ...res, data: arr.map(fromApiPatient) };
};
